// AreaFillOverlay.js — OL postrender canvas pass for CBRN hatch and minefield
// perimeter-stamp fills on tactical-area Polygon features.
//
// Usage:
//   const overlay = new AreaFillOverlay(tacticalLayer, map);
//   overlay.destroy(); // when done
//
// The overlay hooks into the vector layer's 'postrender' event, which fires
// after OL has already drawn all the vector geometry onto the map canvas.
// For every Polygon feature whose SIDC resolves to Variant B or C, we draw
// the custom fill on top using the areaFill.js primitives.
//
// The OL Fill style for those features is set to transparent (see tacticalFeatureStyle
// in editor/renderer/markingStyle.js) so the WebRenderer default fill doesn't show through.

import { getFillVariant, getCentroidGlyph } from './fillVariant';
import {
    drawHatchArea,
    drawPerimeterStampArea,
    drawCentroidGlyph,
    shoelaceCentroid,
} from './areaFill';

// Per-type stamp configuration for Variant C
const STAMP_CONFIG = {
    // G?MSPMCF — Confirmed Minefield: only circles at centroid
    MSPMCF: { stamp: 'circles-at-centroid', stride: 12 },
    // G?MSPMBB — Mine Belt: outward ticks
    MSPMBB: { stamp: 'tick-out', stride: 12 },
    // G?MSPMMX — Mixed Minefield: alternating tick + circle
    MSPMMX: { stamp: 'tick-alt-circle', stride: 12 },
    // G?GPGAEN — Encirclement: inward ticks
    GPGAEN: { stamp: 'tick-in', stride: 12 },
};

/**
 * Extract the 6-char function-code key used in STAMP_CONFIG from a SIDC.
 * Returns the slice starting at position 2 (zero-indexed), 6 chars long.
 */
function stampKey(sidc) {
    if (!sidc || sidc.length < 8) return '';
    return sidc.slice(2, 8).toUpperCase();
}

export class AreaFillOverlay {
    /**
     * @param {import('ol/layer/Vector').default} layer  The LYR_TACTICAL vector layer
     * @param {import('ol/Map').default} map
     */
    constructor(layer, map) {
        this._layer = layer;
        this._map = map;
        this._onPostrender = this._onPostrender.bind(this);
        layer.on('postrender', this._onPostrender);
    }

    destroy() {
        this._layer.un('postrender', this._onPostrender);
    }

    _onPostrender(event) {
        const ctx = event.context;
        if (!ctx) return;

        const source = this._layer.getSource();
        if (!source) return;

        const frameState = event.frameState;
        const pixelRatio = frameState.pixelRatio ?? 1;
        const viewState = frameState.viewState;

        // OL pixel-to-coordinate transform for this frame
        const coordinateToPixel = (coord) =>
            this._map.getPixelFromCoordinate(coord);

        source.getFeatures().forEach((feature) => {
            const geom = feature.getGeometry();
            if (!geom) return;

            const geomType = geom.getType();
            if (geomType !== 'Polygon' && geomType !== 'MultiPolygon') return;

            const props = feature.get('_props') || {};
            const sidc = props.tacticalSidc;
            if (!sidc) return;

            const variant = getFillVariant(sidc);
            if (variant === 'solid') return;

            // Get stroke color from OL style for this feature
            const style = this._layer.getStyleFunction()?.(feature, viewState.resolution);
            const styleArr = style ? (Array.isArray(style) ? style : [style]) : [];
            const strokeColor = styleArr[0]?.getStroke?.()?.getColor?.() ?? '#000000';

            const rings = geomType === 'Polygon'
                ? [geom.getCoordinates()[0]]
                : geom.getCoordinates().map((poly) => poly[0]);

            for (const ring3857 of rings) {
                // Convert EPSG:3857 ring to CSS pixel coords
                const screenRing = ring3857
                    .map((coord) => {
                        const px = coordinateToPixel(coord);
                        if (!px) return null;
                        return [px[0] * pixelRatio, px[1] * pixelRatio];
                    })
                    .filter(Boolean);

                if (screenRing.length < 3) continue;

                // Ensure ring is closed
                const first = screenRing[0];
                const last = screenRing[screenRing.length - 1];
                const closed = first[0] === last[0] && first[1] === last[1]
                    ? screenRing
                    : [...screenRing, first];

                if (variant === 'hatch') {
                    drawHatchArea(ctx, closed, strokeColor, 8);
                    const glyph = getCentroidGlyph(sidc);
                    if (glyph) {
                        const center = shoelaceCentroid(closed);
                        drawCentroidGlyph(ctx, center, glyph, 18, strokeColor);
                    }
                } else if (variant === 'perimeter-stamp') {
                    const key = stampKey(sidc);
                    const cfg = STAMP_CONFIG[key] ?? { stamp: 'tick-out', stride: 12 };
                    drawPerimeterStampArea(ctx, closed, { color: strokeColor, ...cfg });
                }
            }
        });
    }
}
